import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { glossaryFaqItems } from '@/mocks/glossaryFaq';
import { glossaryTerms } from '@/mocks/glossary';
import ScrollReveal from '@/components/base/ScrollReveal';

type GlossaryFaqProps = {
  onSelectTerm: (id: string) => void;
};

export default function GlossaryFaq({ onSelectTerm }: GlossaryFaqProps) {
  const [openId, setOpenId] = useState<string>(glossaryFaqItems[0].id);

  const termMap = useMemo(() => new Map(glossaryTerms.map((term) => [term.id, term])), []);

  const toggle = (id: string) => {
    setOpenId((current) => (current === id ? '' : id));
  };

  return (
    <section className="border-t border-background-200 bg-primary-50/50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:gap-16">
          <div className="lg:sticky lg:top-32 lg:self-start">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary-600">
              <span className="h-px w-6 bg-accent-500" />
              คำถามที่พบบ่อย
            </span>
            <h2 className="mt-3 font-heading text-2xl font-semibold leading-tight text-foreground-950 md:text-[34px]">
              คำศัพท์ Forex ที่มือใหม่ยังสงสัย
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              รวมข้อสงสัยที่เจอบ่อยหลังอ่านคำศัพท์แล้ว ทั้งเรื่องหน่วยนับ การคิดต้นทุน และการกำหนดความเสี่ยง
              พร้อมตัวอย่างตัวเลขที่คำนวณตามจริง
            </p>

            <div className="mt-7 rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-question-answer-line text-base" />
                </span>
                ยังไม่เจอคำตอบที่ต้องการ?
              </div>
              <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                กลับไปดูคำศัพท์ทั้ง {glossaryTerms.length} คำที่อธิบายไว้ด้านบน หรือเริ่มปูพื้นฐาน Forex
                ให้ครบก่อน แล้วค่อยกลับมาทบทวนคำศัพท์อีกครั้ง
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <Link
                  to="/forex-basics"
                  className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  อ่าน Forex พื้นฐาน
                  <i className="ri-arrow-right-line text-base" />
                </Link>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {glossaryFaqItems.map((item, index) => {
              const isOpen = openId === item.id;
              const terms = item.termIds
                .map((id) => termMap.get(id))
                .filter((term): term is (typeof glossaryTerms)[number] => Boolean(term));

              return (
                <ScrollReveal key={item.id} delay={index * 60}>
                  <div
                    className={`overflow-hidden rounded-lg border transition-colors ${
                      isOpen
                        ? 'border-primary-300 bg-background-50'
                        : 'border-background-200 bg-background-50 hover:border-background-300'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(item.id)}
                      aria-expanded={isOpen}
                      aria-controls={`faq-panel-${item.id}`}
                      className="flex w-full cursor-pointer items-start justify-between gap-4 px-4 py-4 text-left md:px-6 md:py-5"
                    >
                      <span className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-num text-xs font-semibold ${
                            isOpen ? 'bg-primary-500 text-background-50' : 'bg-primary-100 text-primary-700'
                          }`}
                        >
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="text-sm font-semibold leading-relaxed text-foreground-900 md:text-base">
                          {item.q}
                        </span>
                      </span>
                      <span
                        className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
                          isOpen ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-500'
                        }`}
                      >
                        <i className={`ri-${isOpen ? 'subtract' : 'add'}-line text-base`} />
                      </span>
                    </button>

                    <div
                      id={`faq-panel-${item.id}`}
                      className={`grid transition-all duration-300 ease-out ${
                        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="px-4 pb-5 md:px-6 md:pb-6">
                          <div className="border-l-2 border-accent-300 pl-4 md:ml-[2.5rem]">
                            <p className="text-sm leading-relaxed text-foreground-600">{item.a}</p>
                            <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.a2}</p>

                            <div className="mt-4 flex flex-wrap items-center gap-2">
                              <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                                <i className="ri-bookmark-3-line text-sm text-primary-500" />
                                ดูคำศัพท์ที่เกี่ยวข้อง
                              </span>
                              {terms.map((term) => (
                                <button
                                  key={term.id}
                                  type="button"
                                  onClick={() => onSelectTerm(term.id)}
                                  className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-200 bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-700 transition-colors hover:border-secondary-400 hover:bg-secondary-50 hover:text-secondary-800"
                                >
                                  <i className="ri-arrow-up-line text-xs text-secondary-600" />
                                  {term.term}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}